import {
  BOOKING_DAILY_PRICING_TYPE_ID,
  BOOKING_HOURLY_PRICING_TYPE_ID,
  BOOKING_MONTHLY_PRICING_TYPE_ID,
} from '../constants/bookingQuickDefaults';
import { ISRAEL_COUNTRY_ID } from './assetCountryCurrency';
import { normalizeAssetTypeName } from '../constants/filterableAssetTypes';
import { resolveListingHourlyDailyPrices } from './assetBookingPricingOptions';

export type AssetCreatePriceSuggestion = {
  hourly: number;
  daily: number;
  monthly: number;
  /** `market` = median of comparable active listings; otherwise table estimate. */
  source: 'market' | 'estimate';
  sampleSize: number;
};

export type AssetCreatePriceSuggestionInput = {
  assetTypeId: number | null;
  assetTypeLabel: string | null;
  city: string | null;
  countryId: unknown;
  /** Active listing rows used for market medians (optional). */
  marketRecords?: readonly Record<string, unknown>[];
};

type LocationTier = 'premium' | 'major' | 'standard' | 'other';

type TypePriceBucket =
  | 'private_office'
  | 'open_space'
  | 'conference'
  | 'gym'
  | 'parking'
  | 'podcast'
  | 'lecture'
  | 'clinic'
  | 'lounge'
  | 'classroom'
  | 'workshop'
  | 'default';

/** Base ILS rates before location multiplier (hourly / daily / monthly). */
const BASE_PRICES_ILS: Record<TypePriceBucket, { hourly: number; daily: number; monthly: number }> = {
  private_office: { hourly: 90, daily: 480, monthly: 5200 },
  open_space: { hourly: 45, daily: 220, monthly: 2800 },
  conference: { hourly: 160, daily: 850, monthly: 7000 },
  gym: { hourly: 70, daily: 320, monthly: 3800 },
  parking: { hourly: 18, daily: 90, monthly: 900 },
  podcast: { hourly: 130, daily: 650, monthly: 5500 },
  lecture: { hourly: 220, daily: 1300, monthly: 9000 },
  clinic: { hourly: 110, daily: 520, monthly: 4800 },
  lounge: { hourly: 75, daily: 360, monthly: 4200 },
  classroom: { hourly: 95, daily: 470, monthly: 4200 },
  workshop: { hourly: 110, daily: 560, monthly: 4800 },
  default: { hourly: 80, daily: 400, monthly: 4000 },
};

const LOCATION_MULTIPLIER: Record<LocationTier, number> = {
  premium: 1.25,
  major: 1,
  standard: 0.85,
  other: 1,
};

const PREMIUM_CITY_KEYS = new Set(
  [
    'tel aviv',
    'tel aviv-yafo',
    'tel aviv yafo',
    'תל אביב',
    'תל אביב-יפו',
    'תל אביב יפו',
    'herzliya',
    'herzelia',
    'הרצליה',
    'ramat gan',
    'רמת גן',
    'givatayim',
    'givataim',
    "giv'atayim",
    'גבעתיים',
    "ra'anana",
    'raanana',
    'רעננה',
    'bat yam',
    'בת ים',
    'holon',
    'חולון',
  ].map((s) => normalizeCityKey(s)),
);

const MAJOR_CITY_KEYS = new Set(
  [
    'jerusalem',
    'ירושלים',
    'haifa',
    'חיפה',
    'beer sheva',
    'beersheba',
    "be'er sheva",
    'באר שבע',
    'netanya',
    'נתניה',
    'ashdod',
    'אשדוד',
    'rishon lezion',
    'rishon leziyyon',
    'ראשון לציון',
    'petah tikva',
    'פתח תקווה',
    'פתח תקוה',
  ].map((s) => normalizeCityKey(s)),
);

function coerceNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function normalizeCityKey(city: string): string {
  return city
    .trim()
    .toLowerCase()
    .replace(/קריית/g, 'קרית')
    .replace(/[''`´]/g, '')
    .replace(/[־–—-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function resolveLocationPriceTier(city: string | null, countryId: unknown): LocationTier {
  const country = coerceNumericId(countryId);
  if (!city?.trim()) {
    return country === ISRAEL_COUNTRY_ID ? 'standard' : 'other';
  }
  const key = normalizeCityKey(city);
  if (PREMIUM_CITY_KEYS.has(key)) {
    return 'premium';
  }
  if (MAJOR_CITY_KEYS.has(key)) {
    return 'major';
  }
  if (country === ISRAEL_COUNTRY_ID || country === null) {
    return 'standard';
  }
  return 'other';
}

export function resolveAssetTypePriceBucket(typeLabel: string | null): TypePriceBucket {
  if (!typeLabel?.trim()) {
    return 'default';
  }
  const n = normalizeAssetTypeName(typeLabel);
  if (/private office|משרד פרטי|executive office|serviced office/.test(n)) {
    return 'private_office';
  }
  if (/open space|אופן ספייס|hot desk|shared office|cowork/.test(n)) {
    return 'open_space';
  }
  if (/conference|meeting|ישיב/.test(n)) {
    return 'conference';
  }
  if (/gym|fitness|כושר|training room/.test(n)) {
    return 'gym';
  }
  if (/parking|חניון|garage/.test(n)) {
    return 'parking';
  }
  if (/podcast|פודקסט/.test(n)) {
    return 'podcast';
  }
  if (/lecture|הרצא/.test(n)) {
    return 'lecture';
  }
  if (/clinic|טיפול|medical/.test(n)) {
    return 'clinic';
  }
  if (/lounge|טרקלין|business lounge/.test(n)) {
    return 'lounge';
  }
  if (/classroom|כית/.test(n)) {
    return 'classroom';
  }
  if (/workshop|סדנ/.test(n)) {
    return 'workshop';
  }
  if (/^office$|משרד/.test(n)) {
    return 'private_office';
  }
  return 'default';
}

function roundNice(value: number, step: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return step;
  }
  return Math.max(step, Math.round(value / step) * step);
}

function roundSuggestion(hourly: number, daily: number, monthly: number): {
  hourly: number;
  daily: number;
  monthly: number;
} {
  return {
    hourly: roundNice(hourly, 5),
    daily: roundNice(daily, 10),
    monthly: roundNice(monthly, 50),
  };
}

function median(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1]! + sorted[mid]!) / 2;
  }
  return sorted[mid]!;
}

function assetRecordCityRaw(asset: Record<string, unknown>): string | null {
  const nested = asset.address;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const city = (nested as Record<string, unknown>).city;
    if (typeof city === 'string' && city.trim()) {
      return city.trim();
    }
  }
  if (typeof asset.city === 'string' && asset.city.trim()) {
    return asset.city.trim();
  }
  return null;
}

function assetRecordTypeId(asset: Record<string, unknown>): number | null {
  for (const key of ['asset_type_id', 'assetTypeId', 'type_id'] as const) {
    const n = coerceNumericId(asset[key]);
    if (n !== null) {
      return n;
    }
  }
  const nested = asset.asset_type;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const o = nested as Record<string, unknown>;
    return coerceNumericId(o.id) ?? coerceNumericId(o.asset_type_id) ?? coerceNumericId(o.type_id);
  }
  return null;
}

function assetRecordTypeNames(asset: Record<string, unknown>): string[] {
  const out: string[] = [];
  const add = (raw: unknown) => {
    if (typeof raw === 'string' && raw.trim()) {
      out.push(normalizeAssetTypeName(raw));
    }
  };
  add(asset.asset_type_name);
  add(asset.type_name);
  add(asset.asset_type);
  const nested = asset.asset_type;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const o = nested as Record<string, unknown>;
    add(o.name);
    add(o.label);
    add(o.type_name);
  }
  return out;
}

function sameCity(a: string | null, b: string | null): boolean {
  if (!a?.trim() || !b?.trim()) {
    return false;
  }
  return normalizeCityKey(a) === normalizeCityKey(b);
}

/**
 * Median hourly/daily/monthly from comparable active listings.
 * Prefers same city + type; falls back to same type anywhere when city comps are sparse.
 */
export function marketAssetCreatePrices(
  records: readonly Record<string, unknown>[],
  options: {
    assetTypeId: number | null;
    assetTypeLabel: string | null;
    city: string | null;
  },
): AssetCreatePriceSuggestion | null {
  const typeNorm = options.assetTypeLabel ? normalizeAssetTypeName(options.assetTypeLabel) : '';
  const typeMatches = (asset: Record<string, unknown>) => {
    if (options.assetTypeId !== null) {
      const id = assetRecordTypeId(asset);
      if (id !== null && id === options.assetTypeId) {
        return true;
      }
    }
    if (!typeNorm) {
      return false;
    }
    return assetRecordTypeNames(asset).some(
      (name) => name === typeNorm || name.includes(typeNorm) || typeNorm.includes(name),
    );
  };

  const typed = records.filter(typeMatches);
  if (typed.length === 0) {
    return null;
  }

  const cityMatched = options.city?.trim()
    ? typed.filter((asset) => sameCity(assetRecordCityRaw(asset), options.city))
    : [];
  const pool = cityMatched.length >= 2 ? cityMatched : typed;
  if (pool.length < 2) {
    return null;
  }

  const hourlyVals: number[] = [];
  const dailyVals: number[] = [];
  const monthlyVals: number[] = [];
  for (const asset of pool) {
    const { hourly, daily, monthly } = resolveListingHourlyDailyPrices(asset);
    if (hourly !== null && hourly > 0) {
      hourlyVals.push(hourly);
    }
    if (daily !== null && daily > 0) {
      dailyVals.push(daily);
    }
    if (monthly !== null && monthly > 0) {
      monthlyVals.push(monthly);
    }
  }

  const hourlyMed = median(hourlyVals);
  const dailyMed = median(dailyVals);
  const monthlyMed = median(monthlyVals);
  if (hourlyMed === null && dailyMed === null && monthlyMed === null) {
    return null;
  }

  const rounded = roundSuggestion(
    hourlyMed ?? (dailyMed !== null ? dailyMed / 6 : 80),
    dailyMed ?? (hourlyMed !== null ? hourlyMed * 6 : 400),
    monthlyMed ?? (dailyMed !== null ? dailyMed * 12 : hourlyMed !== null ? hourlyMed * 50 : 4000),
  );

  return {
    ...rounded,
    source: 'market',
    sampleSize: pool.length,
  };
}

/** Table-based estimate from asset type + location tier. */
export function estimateAssetCreatePrices(input: {
  assetTypeLabel: string | null;
  city: string | null;
  countryId: unknown;
}): AssetCreatePriceSuggestion {
  const bucket = resolveAssetTypePriceBucket(input.assetTypeLabel);
  const tier = resolveLocationPriceTier(input.city, input.countryId);
  const base = BASE_PRICES_ILS[bucket];
  const mult = LOCATION_MULTIPLIER[tier];
  const rounded = roundSuggestion(base.hourly * mult, base.daily * mult, base.monthly * mult);
  return {
    ...rounded,
    source: 'estimate',
    sampleSize: 0,
  };
}

/** Prefer market median when enough comps exist; otherwise heuristic estimate. */
export function resolveAssetCreatePriceSuggestion(
  input: AssetCreatePriceSuggestionInput,
): AssetCreatePriceSuggestion {
  const market = input.marketRecords?.length
    ? marketAssetCreatePrices(input.marketRecords, {
        assetTypeId: input.assetTypeId,
        assetTypeLabel: input.assetTypeLabel,
        city: input.city,
      })
    : null;
  if (market) {
    return market;
  }
  return estimateAssetCreatePrices({
    assetTypeLabel: input.assetTypeLabel,
    city: input.city,
    countryId: input.countryId,
  });
}

export function suggestedPriceForPricingTypeId(
  suggestion: AssetCreatePriceSuggestion,
  pricingTypeId: number,
): number | null {
  if (pricingTypeId === BOOKING_HOURLY_PRICING_TYPE_ID) {
    return suggestion.hourly;
  }
  if (pricingTypeId === BOOKING_DAILY_PRICING_TYPE_ID) {
    return suggestion.daily;
  }
  if (pricingTypeId === BOOKING_MONTHLY_PRICING_TYPE_ID) {
    return suggestion.monthly;
  }
  return null;
}

/** Ensure hourly + daily + monthly rows exist and fill suggested amounts (overwrites existing). */
export function applySuggestionToPricingSelection(
  catalogRows: readonly Record<string, unknown>[],
  current: Map<number, { price: number | null }>,
  suggestion: AssetCreatePriceSuggestion,
): Map<number, { price: number | null }> {
  const next = new Map(current);
  const ensureIds = [
    BOOKING_HOURLY_PRICING_TYPE_ID,
    BOOKING_DAILY_PRICING_TYPE_ID,
    BOOKING_MONTHLY_PRICING_TYPE_ID,
  ];
  const catalogIds = new Set(
    catalogRows
      .map((row) => coerceNumericId(row.pricing_type_id ?? row.id))
      .filter((id): id is number => id !== null),
  );
  for (const id of ensureIds) {
    if (!catalogIds.has(id)) {
      continue;
    }
    const price = suggestedPriceForPricingTypeId(suggestion, id);
    if (price === null) {
      continue;
    }
    next.set(id, { price });
  }
  return next;
}
